import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from './constants';

export function DeviationCard({
  deviation,
  isClosed,
  onClose,
  onDelete,
  onEscalate,
  onCreateTask,
  onClick,
  formatDate,
  getSeverityColor,
  getSeverityLabel,
  getStatusColor,
  getStatusLabel,
  getTypeColor,
  getTypeConfig,
  t
}) {
  const [actionsCount, setActionsCount] = useState(null);
  const typeConfig = getTypeConfig(deviation.type);
  const isRUH = deviation.type === 'ruh';
  const isDeviation = deviation.type === 'deviation';

  // RUH categories with translation
  const getRuhCategoryLabel = (category) => {
    return t(`cases.ruhCategories.${category}`) || category;
  };

  // Fetch actions count on mount
  useEffect(() => {
    const fetchActionsCount = async () => {
      try {
        const token = localStorage.getItem('systemklart_token');
        const response = await axios.get(`${API}/ops/deviations/${deviation.id}/actions`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setActionsCount(response.data.actions?.length || 0);
      } catch (error) {
        console.error('Failed to fetch actions count:', error);
      }
    };
    if (!isClosed) {
      fetchActionsCount();
    }
  }, [deviation.id, isClosed]);

  return (
    <div
      className={`bg-slate-900/60 backdrop-blur-sm border border-slate-800/60 rounded-xl p-4 ${isClosed ? 'opacity-60' : ''} hover:border-slate-700 transition-all cursor-pointer`}
      data-testid={`deviation-item-${deviation.id}`}
      onClick={onClick}
    >
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
        <div className="flex-1 min-w-0">
          {/* Type badge */}
          <div className="flex items-center gap-2 mb-2">
            <span className={`text-xs px-2 py-0.5 rounded ${getTypeColor(deviation.type)}`}>
              {typeConfig.label}
            </span>
            {isRUH && deviation.ruh_category && (
              <span className="text-xs px-2 py-0.5 rounded bg-slate-700/50 text-slate-300">
                {getRuhCategoryLabel(deviation.ruh_category)}
              </span>
            )}
            {deviation.escalated_to_deviation_id && (
              <span className="text-xs px-2 py-0.5 rounded bg-purple-500/20 text-purple-400 border border-purple-500/30">
                {t('cases.escalated')}
              </span>
            )}
            {actionsCount !== null && actionsCount > 0 && (
              <span className="text-xs px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-400 border border-emerald-500/30" data-testid={`actions-count-${deviation.id}`}>
                {actionsCount} {t('cases.actions.title').toLowerCase()}
              </span>
            )}
            {isDeviation && actionsCount === 0 && !isClosed && (
              <span className="text-xs px-2 py-0.5 rounded bg-rose-500/20 text-rose-400 border border-rose-500/30">
                {t('cases.missingActions')}
              </span>
            )}
          </div>

          {/* Title or description */}
          <p className={`text-slate-200 mb-2 ${isClosed ? 'line-through text-slate-500' : ''}`}>
            {deviation.title || deviation.description}
          </p>

          {/* Meta info */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-xs px-2 py-0.5 rounded ${getSeverityColor(deviation.severity)}`}>
              {getSeverityLabel(deviation.severity)}
            </span>
            <span className={`text-xs px-2 py-0.5 rounded ${getStatusColor(deviation.status)}`}>
              {getStatusLabel(deviation.status)}
            </span>
            {deviation.location && (
              <span className="text-xs text-slate-500">📍 {deviation.location}</span>
            )}
            {deviation.responsible && (
              <span className="text-xs text-slate-500">👤 {deviation.responsible}</span>
            )}
            {deviation.deadline && (
              <span className="text-xs text-slate-500">📅 {formatDate(deviation.deadline)}</span>
            )}
            {deviation.follow_up_task_id && (
              <span className="text-xs text-cyan-400">✓ {t('cases.hasFollowUpTask')}</span>
            )}
          </div>
        </div>

        {/* Actions */}
        {!isClosed && (
          <div className="flex flex-wrap items-center gap-2 ml-0 mt-2 sm:ml-4 sm:mt-0" onClick={e => e.stopPropagation()}>
            {isRUH && !deviation.escalated_to_deviation_id && (
              <button
                onClick={onEscalate}
                className="px-3 py-1.5 border border-orange-500/50 text-orange-400 rounded-lg text-sm hover:bg-orange-500/10 transition-colors"
                title={t('cases.escalatedToDeviation')}
                data-testid={`escalate-${deviation.id}`}
              >
                {t('cases.escalate')}
              </button>
            )}
            {!deviation.follow_up_task_id && (
              <button
                onClick={onCreateTask}
                className="px-3 py-1.5 border border-cyan-500/50 text-cyan-400 rounded-lg text-sm hover:bg-cyan-500/10 transition-colors"
                title={t('cases.createTask')}
                data-testid={`create-task-${deviation.id}`}
              >
                + {t('cases.createTask')}
              </button>
            )}
            <button
              onClick={onClose}
              className="px-3 py-1.5 bg-emerald-500 text-slate-950 rounded-lg text-sm font-medium hover:bg-emerald-400 transition-colors"
              data-testid={`close-deviation-${deviation.id}`}
            >
              {t('common.close')}
            </button>
            <button
              onClick={onDelete}
              className="px-3 py-1.5 border border-slate-600 text-slate-400 rounded-lg text-sm hover:bg-slate-800 transition-colors"
              data-testid={`delete-deviation-${deviation.id}`}
            >
              {t('common.delete')}
            </button>
          </div>
        )}

        {isClosed && (
          <div className="flex items-center gap-2 ml-4" onClick={e => e.stopPropagation()}>
            <button
              onClick={onDelete}
              className="px-3 py-1.5 border border-slate-600 text-slate-400 rounded-lg text-sm hover:bg-slate-800 transition-colors"
              data-testid={`delete-deviation-${deviation.id}`}
            >
              {t('common.delete')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
